export interface StatisticsPeriod {
    periodStart: string;
    periodEnd: string;
}

/**
 * Période statistique du mois calendaire courant.
 *
 * Le début est toujours le premier jour du mois et la fin correspond à la date
 * de référence. Le mois courant peut donc être incomplet, ce qui est volontaire
 * pour la vue d'ensemble.
 */
export const getCurrentCalendarMonthPeriod = (
    referenceDate = new Date()
): StatisticsPeriod => {
    const currentDate = toLocalDate(referenceDate);
    const periodStart = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
    );

    return {
        periodStart: formatDate(periodStart),
        periodEnd: formatDate(currentDate)
    };
};

/**
 * Mois calendaire complet précédant le mois contenant periodStart.
 *
 * Exemple : periodStart = 2026-08-01
 * => 2026-07-01 / 2026-07-31.
 */
export const getPreviousCalendarMonthPeriod = (
    periodStart: string
): StatisticsPeriod => {
    const [year, month] = periodStart.split("-").map(Number);
    const previousMonthStart = new Date(year, month - 2, 1);
    const previousMonthEnd = new Date(year, month - 1, 0);

    return {
        periodStart: formatDate(previousMonthStart),
        periodEnd: formatDate(previousMonthEnd)
    };
};

/**
 * Retourne les N derniers mois calendaires, du plus ancien au plus récent.
 * Les mois passés sont complets. Le mois courant s'arrête à la date de référence.
 */
export const getCalendarMonthPeriods = (
    count: number,
    referenceDate = new Date()
): StatisticsPeriod[] => {
    if (count <= 0) return [];

    const currentDate = toLocalDate(referenceDate);
    const periods: StatisticsPeriod[] = [];

    for (let offset = count - 1; offset >= 0; offset -= 1) {
        const monthStart = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() - offset,
            1
        );
        const isCurrentMonth = offset === 0;
        const monthEnd = isCurrentMonth
            ? currentDate
            : new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);

        periods.push({
            periodStart: formatDate(monthStart),
            periodEnd: formatDate(monthEnd)
        });
    }

    return periods;
};

export const formatStatisticsMonthLabel = (
    periodStart: string,
    locale = "fr-FR"
): string => {
    const [year, month] = periodStart.split("-").map(Number);
    const date = new Date(year, month - 1, 1);
    const label = new Intl.DateTimeFormat(locale, { month: "short" }).format(date);
    return label.charAt(0).toUpperCase() + label.slice(1).replace(".", "");
};

const toLocalDate = (date: Date): Date => new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
);

const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
